// Aufnahme. Zwei Wege landen als Take im selben Projekt:
//  1. Browser: jedes normale Interface (oder Mikrofon) über getUserMedia, verlustfrei per AudioWorklet,
//     Eingang 1, 2 oder beide (Stereo), ohne Installation.
//  2. SRS Bridge: Dante und alle weiteren Kanäle, nimmt auf dem Rechner verlustfrei auf und lädt hoch.
// Beide Wege werden sample-genau an die Wiedergabe angelegt (Latenzausgleich, siehe takesFor).
import type { Clip, Take, Track } from '@srs/shared';
import { engine } from './engine.ts';
import { useStudio } from '../state/store.ts';
import { uid } from '../state/demo.ts';
import { api, apiBase } from '../api/client.ts';
import { bridge } from '../bridge/client.ts';
import { midiIn } from './midiin.ts';
import { toast } from '../components/ui.tsx';
import { useEdit } from '../state/edit.ts';
import { clearRange, nextTakeNo, snapGrid } from '../edit/ops.ts';
import { beatAt, meterAt, secAfterBeats, spbAt } from '@srs/shared';

// ---------------------------------------------------------------------------
// Einstellungen des eigenen Interfaces (nur auf diesem Rechner, nicht im Song)
export interface InputPrefs {
  deviceId?: string;
  label?: string;
  /** gemessene Gesamtlatenz (Ausgang + Eingang) je Gerätename in ms */
  measured?: Record<string, number>;
  /** Feinkorrektur von Hand in ms (+ = Aufnahme früher anlegen) */
  trimMs?: number;
}
const PREFS = 'srs.audioIn';
export function inputPrefs(): InputPrefs { try { return JSON.parse(localStorage.getItem(PREFS) ?? '{}'); } catch { return {}; } }
export function setInputPrefs(p: Partial<InputPrefs>) { try { localStorage.setItem(PREFS, JSON.stringify({ ...inputPrefs(), ...p })); } catch { /* privat */ } inputListeners.forEach((l) => l()); }
const inputListeners = new Set<() => void>();
export function onInputChange(l: () => void) { inputListeners.add(l); return () => { inputListeners.delete(l); }; }

let stream: MediaStream | null = null;
let node: AudioWorkletNode | null = null;
let src: MediaStreamAudioSourceNode | null = null;
/** Pegel je Eingang (0 … 1), vom Worklet etwa 25-mal pro Sekunde */
export const inputLevel = { peak: 0, peaks: [0, 0] as number[] };
/** Zustand des geöffneten Eingangs */
export const inputInfo = { label: '', channels: 0, sampleRate: 0, inputLatencyMs: 0 };

export async function listInputs() {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}

/** Interface öffnen. Ohne Angabe das zuletzt gewählte, sonst das Standardgerät. */
export async function openInput(deviceId = inputPrefs().deviceId) {
  const ctx = await engine.ensure();
  stream?.getTracks().forEach((t) => t.stop());
  src?.disconnect();
  // Keine Bearbeitung durch den Browser: das Signal soll unverändert ankommen
  const audio: MediaTrackConstraints = { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: { ideal: 2 }, sampleRate: { ideal: ctx.sampleRate } };
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { ...audio, deviceId: deviceId ? { exact: deviceId } : undefined } });
  } catch (e) {
    // gespeichertes Gerät nicht mehr da: Standardgerät
    if (!deviceId) throw e;
    stream = await navigator.mediaDevices.getUserMedia({ audio });
  }
  const track = stream.getAudioTracks()[0];
  const s = track?.getSettings() as MediaTrackSettings & { latency?: number };
  src = ctx.createMediaStreamSource(stream);
  node ??= new AudioWorkletNode(ctx, 'srs-recorder', { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'discrete' });
  node.port.onmessage = (e) => {
    if (e.data.type !== 'level') return;
    inputLevel.peaks = e.data.peaks; inputLevel.peak = Math.max(...e.data.peaks);
    // Live-Wellenform der laufenden Aufnahme (gehörte Zeit = Position minus Latenz)
    if (liveRec.on && useStudio.getState().transport.playing) {
      const t = engine.position - browserLatency().sec, last = liveRec.wave[liveRec.wave.length - 1];
      if (last && t < last.t - 0.05) liveRec.wave = [];   // Loop: neuer Durchgang
      liveRec.wave.push({ t, pk: e.data.peaks });
    }
  };
  src.connect(node);
  Object.assign(inputInfo, { label: track?.label ?? 'Eingang', channels: s?.channelCount ?? 1, sampleRate: s?.sampleRate ?? ctx.sampleRate, inputLatencyMs: Math.round((s?.latency ?? 0) * 1000 * 10) / 10 });
  setInputPrefs({ deviceId: s?.deviceId ?? deviceId, label: inputInfo.label });
  return inputInfo.label;
}
export function closeInput() { stream?.getTracks().forEach((t) => t.stop()); stream = null; src?.disconnect(); src = null; inputInfo.label = ''; inputListeners.forEach((l) => l()); }
export const inputOpen = () => !!stream && stream.getAudioTracks().some((t) => t.readyState === 'live');

/** Latenz, um die eine Browser-Aufnahme verschoben wird: gemessen, sonst geschätzt (in Sekunden) */
export function browserLatency(): { sec: number; measured: boolean } {
  const p = inputPrefs(), m = p.measured?.[inputInfo.label || p.label || ''];
  const trim = (p.trimMs ?? 0) / 1000;
  if (m !== undefined) return { sec: m / 1000 + trim, measured: true };
  const ctx = engine.ctx;
  const est = (ctx?.baseLatency ?? 0) + ((ctx as (AudioContext & { outputLatency?: number }) | null)?.outputLatency ?? 0) + inputInfo.inputLatencyMs / 1000;
  return { sec: est + trim, measured: false };
}

/** Kanäle eines Takes aus der Browser-Aufnahme für eine Spur: In 1, In 2 oder beide */
export function channelsFor(t: Track, available: number): number[] {
  const inp = t.input?.kind === 'browser' ? t.input : { kind: 'browser' as const };
  const ch = Math.min(inp.ch ?? 0, Math.max(0, available - 1));
  if (inp.stereo && available >= 2) return [ch, Math.min(ch + 1, available - 1)];
  return [ch];
}
export function inputLabel(t: Track) {
  if (t.input?.kind === 'bridge') return t.input.label ?? `Bridge ${t.input.channel + 1}`;
  if (t.input?.kind === 'remote') return 'Remote';
  const ch = (t.input?.kind === 'browser' ? t.input.ch : 0) ?? 0;
  return t.input?.kind === 'browser' && t.input.stereo ? `In ${ch + 1}+${ch + 2}` : `In ${ch + 1}`;
}

// ---------------------------------------------------------------------------
// Aufnahme wie in Pro Tools (Kap. 22–25):
//  · ohne Auswahl ab der Einfügemarke, mit Auswahl als Punch-In/-Out (nur die Auswahl wird ersetzt)
//  · Pre-/Post-Roll in Takten, Vorzähler
//  · Loop-Recording: jeder Durchgang ein Take (in den Lanes zum Comping), der letzte liegt oben
//  · gleichzeitig Browser-Interface, Bridge (Dante) und MIDI-Keyboard
export interface RecPlan { from: number; recStart: number; punchOut?: number; loop?: { start: number; end: number } }
export interface Pass { k: number; start: number; end: number; complete: boolean }
interface Active {
  plan: RecPlan;
  browser: Track[]; midi: Track[];
  bridge?: { takeId: string; inputs: { channel: number; trackId: string }[]; startedAt?: number };
  by: string;
}
let active: Active | null = null;
let finishing = false;

/** Laufende Aufnahme für die Anzeige: welche Spuren, ab wo, Live-Wellenform der Browser-Eingänge */
export interface LiveWave { t: number; pk: number[] }
export const liveRec: {
  on: boolean; trackIds: string[]; browserIds: string[]; start: number; loop?: { start: number; end: number }; punchOut?: number;
  /** Browser-Eingang: Spitzen je Kanal */
  wave: LiveWave[];
  /** Bridge: Spitzen je Meter-Index, Spur → Index */
  bridgeWave: LiveWave[]; bridgeIdx: Record<string, number>;
  /** Nach dem Stopp: Bridge-Takes, die noch gesichert/übertragen werden (Spur → Bereich und Zustand) */
  pending: Record<string, { a: number; b: number; text: string; err?: boolean }>;
} = { on: false, trackIds: [], browserIds: [], start: 0, wave: [], bridgeWave: [], bridgeIdx: {}, pending: {} };

// Pegel der Bridge während der Aufnahme als Live-Wellenform sammeln
let meterChannels: number[] = [];
bridge.on((m) => {
  if (m.type === 'uploadProgress') {
    for (const [id, pd] of Object.entries(liveRec.pending)) if (bridgeTake?.takeId === m.takeId && bridgeTake.inputs.some((i) => i.trackId === id && i.channel === m.channel) && !pd.err) pd.text = `Bridge: wird übertragen … ${Math.round(m.percent)} %`;
    return;
  }
  if (m.type !== 'meters' || !liveRec.on || !Object.keys(liveRec.bridgeIdx).length || !useStudio.getState().transport.playing) return;
  const t = engine.position, last = liveRec.bridgeWave[liveRec.bridgeWave.length - 1];
  if (last && t < last.t - 0.05) liveRec.bridgeWave = [];   // Loop: neuer Durchgang
  liveRec.bridgeWave.push({ t, pk: m.peak ?? m.levels });
});
let bridgeTake: Active['bridge'] | null = null;

/** Durchgänge aus den Wiedergabe-Abschnitten (ein Abschnitt je Loop-Durchgang) */
export function passesOf(plan: RecPlan, segs: { ctx: number; pos: number }[], stopPos: number): Pass[] {
  if (!plan.loop) {
    const end = Math.min(plan.punchOut ?? Infinity, stopPos);
    return end - plan.recStart > 0.02 ? [{ k: 0, start: plan.recStart, end, complete: true }] : [];
  }
  const L = plan.loop, out: Pass[] = [];
  segs.forEach((sg, k) => {
    const last = k === segs.length - 1;
    const a = Math.max(sg.pos, L.start), b = last ? Math.min(stopPos, L.end) : L.end;
    if (b - a > 0.05) out.push({ k, start: a, end: b, complete: !last || stopPos >= L.end - 0.01 });
  });
  return out;
}

/** Takes und Haupt-Clip aus den Durchgängen. srcSec(P, k): Sekunde in der Quelldatei, an der Projektposition P im Durchgang k liegt. */
export function takesFor(plan: RecPlan, passes: Pass[], srcSec: (P: number, k: number) => number, srcLen: number, stopPos: number) {
  const takes: { start: number; duration: number; offset: number }[] = [];
  let main: { start: number; duration: number; offset: number; take: number } | null = null;
  const clampTake = (start: number, end: number, k: number) => {
    let off = srcSec(start, k), st = start;
    if (off < 0) { st -= off; off = 0; }
    const dur = Math.min(end - st, srcLen - off);
    return dur > 0.02 ? { start: st, duration: dur, offset: off } : null;
  };
  if (!plan.loop) {
    if (!passes.length) return { takes, main };
    // Take mit Vorlauf (Pre-Roll) als Griff, Clip nur im Punch-Bereich
    const tk = clampTake(Math.min(plan.from, plan.recStart), stopPos, 0);
    if (!tk) return { takes, main };
    takes.push(tk);
    const p = passes[0], st = Math.max(p.start, tk.start), end = Math.min(p.end, tk.start + tk.duration);
    if (end - st > 0.02) main = { start: st, duration: end - st, offset: tk.offset + (st - tk.start), take: 0 };
    return { takes, main };
  }
  passes.forEach((p) => { const tk = clampTake(p.start, p.end, p.k); if (tk) takes.push(tk); });
  if (takes.length) {
    const completeIdx = passes.map((p, i) => (p.complete && i < takes.length ? i : -1)).filter((i) => i >= 0);
    const i = completeIdx.length ? completeIdx[completeIdx.length - 1] : takes.length - 1;
    main = { ...takes[i], take: i };
  }
  return { takes, main };
}

/** Wanduhrzeit (ms, Systemuhr wie Date.now und die Bridge), zu der die Kontextzeit t aus den Lautsprechern/Kopfhörern kommt.
 *  Bewusst über Date.now verankert: performance.timeOrigin + performance.now() bleibt auf dem Mac im Ruhezustand stehen
 *  und geht danach gegenüber der Systemuhr der Bridge nach (Minuten bis Stunden). */
export function wallOfCtx(t: number): number {
  const ctx = engine.ctx!, ts = ctx.getOutputTimestamp?.(), now = Date.now(), pn = performance.now();
  if (!ts || !ts.performanceTime || ts.contextTime === undefined) return now + (t - ctx.currentTime) * 1000 + ((ctx.baseLatency ?? 0) + ((ctx as AudioContext & { outputLatency?: number }).outputLatency ?? 0)) * 1000;
  return now + (ts.performanceTime - pn) + (t - ts.contextTime) * 1000;
}
/** Wanduhrzeit von Sample 0 einer Bridge-Datei: aus recordStarted, gegengeprüft über das Dateiende (Stopp-Zeitpunkt minus Länge) */
export function bridgeSample0(startedAt: number | undefined, stopWall: number, durSec: number, inLatSec: number) {
  const fromEnd = stopWall - (durSec + inLatSec) * 1000;
  if (startedAt === undefined) return { a0: fromEnd, used: 'end' as const };
  const fromStart = startedAt - inLatSec * 1000;
  // Mehr als 0,75 s Abweichung: Uhren passen nicht zusammen, dem Dateiende vertrauen
  return Math.abs(fromStart - fromEnd) > 750 ? { a0: fromEnd, used: 'end' as const } : { a0: fromStart, used: 'start' as const };
}
/** Gehörte Kontextzeit zu einem performance.now()-Zeitstempel (MIDI) */
function ctxOfPerf(perf: number): number {
  const ctx = engine.ctx!, ts = ctx.getOutputTimestamp?.();
  if (!ts || ts.contextTime === undefined || ts.performanceTime === undefined) return ctx.currentTime - (performance.now() - perf) / 1000;
  return ts.contextTime + (perf - ts.performanceTime) / 1000;
}

/** Startet Wiedergabe (mit Vorzähler, Pre-Roll) und Aufnahme auf allen scharf geschalteten Spuren */
export async function startRecording() {
  const st = useStudio.getState();
  const tracks = st.project.tracks.filter((t) => t.armed && !t.hidden);
  const browser = tracks.filter((t) => t.kind === 'audio' && (t.input?.kind ?? 'browser') === 'browser');
  const bridgeArmed = tracks.filter((t) => t.kind === 'audio' && t.input?.kind === 'bridge');
  const midi = tracks.filter((t) => t.kind === 'midi');
  if (!browser.length && !bridgeArmed.length && !midi.length) throw new Error('Keine Spur scharf geschaltet (Taste ● am Spurkopf)');
  if (bridgeArmed.length && !bridge.status.connected) throw new Error('Bridge nicht verbunden (Eingänge über Bridge/Dante)');
  if (bridgeArmed.length) {
    const dev = bridge.status.device;
    if (!dev) throw new Error('In der Bridge ist kein Audiogerät geöffnet. Unter „Audio“ → Bridge ein Gerät wählen.');
    const chans = new Set(dev.inputs.map((c) => c.channel));
    const bad = bridgeArmed.find((t) => !chans.has((t.input as { channel: number }).channel));
    if (bad) throw new Error(`${bad.name}: Eingang ${inputLabel(bad)} gibt es am Gerät „${dev.name}“ nicht. Bitte am Spurkopf einen Eingang wählen.`);
    if (!useStudio.getState().token) throw new Error('Bridge-Aufnahme braucht eine Anmeldung am Studio-Server.');
  }
  if (browser.length && !inputOpen()) await openInput();
  if (midi.length && !midiIn.access) await midiIn.enable();
  const ctx = await engine.ensure();
  const p = st.project, tr = st.transport;
  /** n Takte vor bzw. nach einer Stelle (Tempo und Taktart dort) */
  const bars = (at: number, n: number) => secAfterBeats(p, at, n * meterAt(p, at).barLen);

  // Bereich: Loop-Recording, Punch (Auswahl) oder ab Einfügemarke
  const r = useEdit.getState().range;
  const sel = r && r.end - r.start > 0.05 ? r : null;
  let plan: RecPlan;
  // Loop-Aufnahme mit ⌥L oder wenn der Loop-Schalter (L) an ist: Auswahl bzw. Loop-Bereich wiederholt sich, jeder Durchgang ein Take
  if (tr.loopRecord || p.loop.enabled) {
    const L = sel ? { start: sel.start, end: sel.end } : p.loop.end - p.loop.start > 0.05 ? { start: p.loop.start, end: p.loop.end } : { start: tr.position, end: bars(tr.position, 4) };
    if (!p.loop.enabled || Math.abs(p.loop.start - L.start) > 1e-3 || Math.abs(p.loop.end - L.end) > 1e-3) st.commit('Loop-Aufnahme', (q) => { q.loop = { enabled: true, start: L.start, end: L.end }; });
    plan = { from: Math.max(0, bars(L.start, -(tr.preRoll ?? 0))), recStart: L.start, loop: L };
  } else if (sel) {
    plan = { from: Math.max(0, bars(sel.start, -(tr.preRoll ?? 0))), recStart: sel.start, punchOut: sel.end };
  } else {
    plan = { from: Math.max(0, bars(tr.position, -(tr.preRoll ?? 0))), recStart: tr.position };
  }
  const until = plan.punchOut !== undefined ? bars(plan.punchOut, tr.postRoll ?? 0) : undefined;

  // Vorzähler: ein Takt in Taktart und Tempo am Startpunkt
  const mt = meterAt(p, plan.from), unit = spbAt(p, plan.from) * 4 / mt.den;
  const countIn = tr.countIn ? mt.num * unit : 0;
  if (countIn) for (let b = 0; b < mt.num; b++) engine.click(ctx.currentTime + 0.05 + b * unit, b === 0);
  // Bridge zuerst starten (läuft parallel zum Vorzähler); meldet sie einen Fehler, startet die Aufnahme nicht
  let take: Active['bridge'] | undefined, bridgeStart: Promise<void> | null = null, tk: NonNullable<Active['bridge']> | null = null;
  if (bridgeArmed.length) {
    tk = { takeId: uid('take'), inputs: bridgeArmed.map((t) => ({ channel: (t.input as { channel: number }).channel, trackId: t.id })) } as NonNullable<Active['bridge']>;
    meterChannels = bridge.status.device!.inputs.map((c) => c.channel);
    bridge.send({ type: 'subscribeMeters', channels: meterChannels, fps: 30 });
    const id = bridge.send({ type: 'recordStart', takeId: tk.takeId, inputs: tk.inputs });
    const t0 = tk;
    bridgeStart = bridge.waitFor('recordStarted', (m) => m.takeId === t0.takeId, 5000, id).then((m) => { t0.startedAt = m.startedAt; take = t0; });
  }
  await new Promise((res) => setTimeout(res, countIn * 1000));
  if (bridgeStart) {
    try { await bridgeStart; } catch (e) {
      if (tk) bridge.send({ type: 'recordStop', takeId: tk.takeId });   // falls sie doch noch anläuft
      throw new Error(`Bridge-Aufnahme startet nicht: ${(e as Error).message}`);
    }
  }

  active = { plan, browser, midi, by: st.user.id };
  if (take) { active.bridge = take; bridgeTake = take; }
  const bridgeIdx: Record<string, number> = {};
  for (const i of take?.inputs ?? []) bridgeIdx[i.trackId] = meterChannels.indexOf(i.channel);
  Object.assign(liveRec, { on: true, trackIds: [...browser, ...bridgeArmed, ...midi].map((t) => t.id), browserIds: browser.map((t) => t.id), start: plan.recStart, loop: plan.loop, punchOut: plan.punchOut, wave: [], bridgeWave: [], bridgeIdx });
  engine.loopRecording = !!plan.loop;
  toast(plan.loop ? 'Loop-Aufnahme: läuft im Kreis, jeder Durchgang wird ein Take. Stopp mit der Leertaste.' : plan.punchOut !== undefined ? 'Punch-In/-Out: nur die Auswahl wird aufgenommen, danach stoppt die Aufnahme.' : 'Aufnahme läuft. Stopp mit der Leertaste.');
  st.setTransport({ recording: true });
  if (browser.length) node!.port.postMessage('start');
  if (midi.length) midiIn.startRecording();
  await engine.play(plan.from, until);
}

/** Aufnahme beenden (Stopp-Taste). Auch der automatische Stopp nach Punch/Post-Roll landet hier. */
export async function stopRecording() {
  if (!active) { engine.stop(); return; }
  if (useStudio.getState().transport.playing) engine.stop(); else await finishRecording();
}
engine.onStop(() => { if (active && !finishing) void finishRecording(); });

async function finishRecording() {
  const rec = active;
  if (!rec || finishing) return;
  finishing = true;
  active = null;
  const wasFrom = liveRec.loop ? liveRec.loop.start : liveRec.start, punch = liveRec.punchOut;
  liveRec.on = false; liveRec.wave = []; liveRec.bridgeWave = []; liveRec.bridgeIdx = {};
  try {
    const segs = engine.segments.slice(), stopPos = engine.posAtCtx(engine.lastStopCtx);
    const passes = passesOf(rec.plan, segs, stopPos);
    if (rec.midi.length) finishMidi(rec, segs, passes);
    if (rec.bridge) {
      // Platzhalter, bis der Take von der Bridge da ist (sichtbar statt stiller Wartezeit)
      const b = Math.min(stopPos, punch ?? Infinity);
      for (const i of rec.bridge.inputs) liveRec.pending[i.trackId] = { a: wasFrom, b, text: 'Bridge: Aufnahme wird gesichert …' };
      const ids = rec.bridge.inputs.map((i) => i.trackId);
      void finishBridge(rec, rec.bridge, segs, passes, stopPos).catch((e) => {
        const msg = `Bridge-Aufnahme fehlgeschlagen: ${(e as Error).message}`;
        console.warn(msg); toast(msg, 12000);
        for (const id of ids) if (liveRec.pending[id]) Object.assign(liveRec.pending[id], { text: msg, err: true });
        setTimeout(() => { for (const id of ids) if (liveRec.pending[id]?.err) delete liveRec.pending[id]; }, 12000);
      });
    }
    if (rec.browser.length) await finishBrowser(rec, segs, passes, stopPos);
    if (rec.plan.loop && (rec.browser.length || rec.bridge)) {
      // Takes als Lanes zeigen, damit man direkt comped
      const e = useEdit.getState(), ids = [...rec.browser.map((t) => t.id), ...(rec.bridge?.inputs.map((i) => i.trackId) ?? [])];
      e.set({ lanes: [...new Set([...e.lanes, ...ids])] });
    }
  } finally { finishing = false; }
}

/** Takes einer Spur anlegen und den Haupt-Clip setzen (ersetzt im Bereich, wie Pro Tools) */
function commitAudio(trackId: string, sourceId: string, source: Take['source'], latencyMs: number, res: ReturnType<typeof takesFor>, by: string) {
  if (!res.main) return;
  const main = res.main, at = new Date().toISOString();
  useStudio.getState().commit('Aufnahme', (p) => {
    const t = p.tracks.find((x) => x.id === trackId);
    if (!t) return;
    let n = nextTakeNo(p, t.id) - 1;
    const ids = res.takes.map((tk) => {
      const take: Take = { id: uid('take'), trackId: t.id, n: ++n, by, source, sourceId, start: tk.start, duration: tk.duration, offset: tk.offset, latencyMs, createdAt: at };
      p.takes.push(take);
      return take;
    });
    const tk = ids[main.take];
    clearRange(p, { start: main.start, end: main.start + main.duration, trackIds: [t.id] }, false);
    const clip: Clip = { id: uid('c'), trackId: t.id, kind: 'audio', name: `${t.name} Take ${tk.n}`, start: main.start, offset: main.offset, duration: main.duration, sourceId, fadeIn: 0.005, fadeOut: 0.01, takeId: tk.id, by: { userId: by, at } };
    p.clips.push(clip);
  });
}

async function finishBrowser(rec: Active, segs: { ctx: number; pos: number }[], passes: Pass[], stopPos: number) {
  const ctx = engine.ctx!, lat = browserLatency();
  // Nach dem Stopp noch um die Latenz weiter aufnehmen, sonst fehlt das Ende (das Gespielte kommt verzögert zurück)
  await new Promise((r) => setTimeout(r, (lat.sec + 0.06) * 1000));
  const data = await new Promise<{ channels: Float32Array[]; startFrame: number; sampleRate: number }>((resolve) => {
    const prev = node!.port.onmessage;
    node!.port.onmessage = (e) => { if (e.data.type === 'data') { node!.port.onmessage = prev; resolve(e.data); } else prev?.call(node!.port, e); };
    node!.port.postMessage('stop');
  });
  if (!data.channels.length || !data.channels[0].length) return;
  const sr = data.sampleRate || ctx.sampleRate, T0 = Math.max(0, data.startFrame) / sr;
  const len = data.channels[0].length / sr;
  // Projektposition P im Durchgang k wurde zur Kontextzeit seg.ctx + (P − seg.pos) gespielt und kam um die Latenz später zurück
  const srcSec = (P: number, k: number) => segs[k].ctx + (P - segs[k].pos) + lat.sec - T0;
  const res = takesFor(rec.plan, passes, srcSec, len, stopPos);
  if (!res.main) return;
  const st = useStudio.getState();
  const bySpec = new Map<string, { buf: AudioBuffer; sourceId: string }>();
  for (const t of rec.browser) {
    const chans = channelsFor(t, data.channels.length), key = chans.join('+');
    let spec = bySpec.get(key);
    if (!spec) {
      const buf = ctx.createBuffer(chans.length, data.channels[0].length, sr);
      chans.forEach((c, i) => buf.copyToChannel(data.channels[c] as Float32Array<ArrayBuffer>, i));
      spec = { buf, sourceId: uid('src') };
      engine.buffers.set(spec.sourceId, buf);
      bySpec.set(key, spec);
    }
    commitAudio(t.id, spec.sourceId, 'browser', Math.round(lat.sec * 1000), res, rec.by);
  }
  // Upload im Hintergrund, damit die anderen im Raum den Take hören
  if (st.token) for (const spec of bySpec.values()) void api.uploadWav(encodeWav(spec.buf), `${spec.sourceId}.wav`, st.project.id).then((m) => {
    if (!m) return;
    engine.buffers.set(m.id, spec.buf);
    useStudio.getState().replaceSourceId(spec.sourceId, m.id);
  }).catch(() => undefined);
}

async function finishBridge(rec: Active, take: NonNullable<Active['bridge']>, segs: { ctx: number; pos: number }[], passes: Pass[], stopPos: number) {
  const st = useStudio.getState();
  const walls = segs.map((sg) => wallOfCtx(sg.ctx));   // Wanduhr jetzt bestimmen, solange der Kontext läuft
  // Nachlauf um die Roundtrip-Latenz, damit das Ende vollständig ist
  await new Promise((r) => setTimeout(r, Math.min(500, (bridge.status.latencyMs ?? 20) + 60)));
  const stopWall = Date.now();
  const stopId = bridge.send({ type: 'recordStop', takeId: take.takeId });
  const stopped = await bridge.waitFor('recordStopped', (m) => m.takeId === take.takeId, 15000, stopId);
  if (!stopped.files.length) throw new Error('die Bridge hat keine Datei geschrieben');
  if (!st.token) throw new Error('nicht am Studio-Server angemeldet');
  const pend = (trackId: string, text: string) => { if (liveRec.pending[trackId]) liveRec.pending[trackId].text = text; };
  for (const f of stopped.files) pend(f.trackId, 'Bridge: wird übertragen …');
  const upId = bridge.send({ type: 'uploadTake', takeId: take.takeId, uploadUrl: `${apiBase()}/uploads`, authToken: st.token, projectId: st.project.id });
  for (const f of stopped.files) {
    const done = await bridge.waitFor('uploadDone', (m) => m.takeId === take.takeId && m.channel === f.channel, 300000, upId);
    pend(f.trackId, 'Bridge: Take wird geladen …');
    const buf = await engine.decode(done.sourceId, await api.media(done.sourceId));
    const inLat = f.offsetSamples / f.sampleRate;
    // Sample 0 lag zur Wanduhrzeit startedAt − Eingangslatenz am Wandler; P im Durchgang k war zur Zeit walls[k] + (P − seg.pos) zu hören
    const { a0: A0, used } = bridgeSample0(take.startedAt, stopWall, f.frames / f.sampleRate, inLat);
    if (used === 'end' && take.startedAt !== undefined) console.warn(`Bridge-Zeitstempel weicht um ${Math.round(take.startedAt - inLat * 1000 - A0)} ms ab, Take über das Dateiende ausgerichtet`);
    const srcSec = (P: number, k: number) => (walls[k] + (P - segs[k].pos) * 1000 - A0) / 1000;
    const res = takesFor(rec.plan, passes, srcSec, buf.duration, stopPos);
    if (!res.main) throw new Error(`Take (${buf.duration.toFixed(1)} s) passt nicht in den Aufnahmebereich, Zeitabgleich fehlgeschlagen`);
    commitAudio(f.trackId, done.sourceId, 'bridge', Math.round(inLat * 1000), res, rec.by);
    delete liveRec.pending[f.trackId];
  }
  if (bridgeTake === take) bridgeTake = null;
}

/** MIDI-Aufnahme anlegen: Noten auf die gehörte Projektzeit, im Loop je Durchgang, Merge oder Ersetzen, Input Quantize */
function finishMidi(rec: Active, segs: { ctx: number; pos: number }[], passes: Pass[]) {
  const raw = midiIn.stopRecording();
  if (!raw.length || !passes.length) return;
  const st = useStudio.getState(), p = st.project, tr = st.transport;
  const beatsFrom = (a: number, b: number) => beatAt(p, b) - beatAt(p, a);
  const segOf = (c: number) => { let k = 0; segs.forEach((sg, i) => { if (sg.ctx <= c + 1e-9) k = i; }); return k; };
  const grid = tr.inputQuantize ? useEdit.getState().grid : null;
  type N = { pos: number; dur: number; m: number; v: number; k: number };
  let notes: N[] = raw.map((n) => { const c = ctxOfPerf(n.on), k = segOf(c); return { pos: segs[k].pos + (c - segs[k].ctx), dur: (n.off - n.on) / 1000, m: n.m, v: n.v, k }; });
  if (grid) notes = notes.map((n) => ({ ...n, pos: snapGrid(p, n.pos, grid) }));
  const merge = !!tr.midiMerge;
  // Bereich der Clips
  const loop = rec.plan.loop;
  const region = loop ? { start: loop.start, end: loop.end } : { start: passes[0].start, end: passes[0].end };
  let keep: N[];
  if (loop) {
    const complete = passes.filter((x) => x.complete).map((x) => x.k);
    const lastK = complete.length ? complete[complete.length - 1] : passes[passes.length - 1].k;
    keep = notes.filter((n) => n.pos >= loop.start - 0.02 && n.pos < loop.end && (merge || n.k === lastK));
  } else keep = notes.filter((n) => n.pos >= region.start - 0.02 && n.pos < region.end);
  if (!keep.length) return;
  const at = new Date().toISOString();
  st.commit('MIDI-Aufnahme', (q2) => {
    for (const tr0 of rec.midi) {
      const t = q2.tracks.find((x) => x.id === tr0.id);
      if (!t) continue;
      const host = merge ? q2.clips.find((c) => c.trackId === t.id && c.kind === 'midi' && c.start <= region.start + 1e-6 && c.start + c.duration >= region.start - 1e-6) : undefined;
      if (host) {
        // Merge: in den vorhandenen Clip einfügen, Clip bei Bedarf verlängern
        host.notes ??= [];
        for (const n of keep) host.notes.push({ t: beatsFrom(host.start, Math.max(n.pos, host.start)), d: Math.max(0.05, beatsFrom(n.pos, n.pos + n.dur)), m: n.m, v: n.v, by: { userId: rec.by, at } });
        host.duration = Math.max(host.duration, region.end - host.start);
        continue;
      }
      if (!merge) clearRange(q2, { start: region.start, end: region.end, trackIds: [t.id] }, false);
      q2.clips.push({ id: uid('c'), trackId: t.id, kind: 'midi', name: t.name, start: region.start, offset: 0, duration: region.end - region.start, fadeIn: 0, fadeOut: 0, by: { userId: rec.by, at },
        notes: keep.map((n) => ({ t: beatsFrom(region.start, Math.max(n.pos, region.start)), d: Math.max(0.05, beatsFrom(n.pos, n.pos + Math.min(n.dur, region.end - n.pos))), m: n.m, v: n.v, by: { userId: rec.by, at } })) });
    }
  });
}

// ---------------------------------------------------------------------------
// Input Monitoring (Pro Tools: Taste I): Eingang über die Spur abhören, mit ihren Inserts.
// Im Browser hört man das mit etwas Verzögerung; für Gesang besser das Direct Monitoring des Interfaces.
const monitors = new Map<string, AudioNode[]>();
export const isMonitoring = (trackId: string) => monitors.has(trackId);
export async function setMonitoring(t: Track, on: boolean) {
  for (const n of monitors.get(t.id) ?? []) n.disconnect();
  monitors.delete(t.id);
  if (on) {
    if (!inputOpen()) await openInput();
    const ctx = await engine.ensure();
    const split = ctx.createChannelSplitter(2), g = ctx.createGain();
    src!.connect(split);
    for (const c of channelsFor(t, inputInfo.channels || 1)) split.connect(g, c);
    g.connect(engine.graph!.strip(t.id).input);
    monitors.set(t.id, [split, g]);
  }
  inputListeners.forEach((l) => l());
}

// ---------------------------------------------------------------------------
// Latenz messen (Browser): kurzes Messsignal ausgeben und über den Eingang wieder aufnehmen.
// Dafür Ausgang und Eingang des Interfaces mit einem Kabel verbinden (Loopback) oder das Mikrofon vor die Boxen halten.
export async function measureBrowserLatency(): Promise<{ ms: number; quality: number }> {
  if (!inputOpen()) await openInput();
  const ctx = await engine.ensure();
  if (ctx.state !== 'running') await ctx.resume();
  const sr = ctx.sampleRate;
  // Messsignal: Chirp 300 Hz → 6 kHz, 40 ms, mit Hann-Fenster (gut hörbar, eindeutig wiederzufinden)
  const n = Math.round(0.04 * sr), probe = new Float32Array(n);
  for (let i = 0; i < n; i++) { const u = i / n, f = 300 + (6000 - 300) * u / 2; probe[i] = 0.9 * Math.sin(2 * Math.PI * f * (i / sr)) * (0.5 - 0.5 * Math.cos(2 * Math.PI * u)); }
  const buf = ctx.createBuffer(1, n, sr); buf.copyToChannel(probe, 0);
  const good: number[] = [];
  let bestQ = 0;
  for (let k = 0; k < 4; k++) {
    node!.port.postMessage('start');
    const tp = ctx.currentTime + 0.25;
    const s = ctx.createBufferSource(); s.buffer = buf; s.connect(ctx.destination); s.start(tp);
    await new Promise((r) => setTimeout(r, 900));
    const data = await new Promise<{ channels: Float32Array[]; startFrame: number }>((resolve) => {
      const prev = node!.port.onmessage;
      node!.port.onmessage = (e) => { if (e.data.type === 'data') { node!.port.onmessage = prev; resolve(e.data); } else prev?.call(node!.port, e); };
      node!.port.postMessage('stop');
    });
    if (data.startFrame < 0 || !data.channels.length) continue;
    const r = findProbe(data.channels, probe);
    bestQ = Math.max(bestQ, r.quality);
    // nur eindeutige Treffer zählen (sonst findet die Korrelation zufällige Stellen im Rauschen)
    if (r.idx >= 0 && r.quality >= 0.3) good.push((data.startFrame + r.idx) / sr - tp);
  }
  if (!good.length) throw new Error(`Messsignal nicht gehört (Erkennung ${Math.round(bestQ * 100)} %). Lautstärke der Boxen erhöhen und Kopfhörer abnehmen, oder einen Ausgang des Interfaces per Kabel mit einem Eingang verbinden.`);
  good.sort((a, b) => a - b);
  const med = good[Math.floor(good.length / 2)];
  const ms = Math.round(med * 1000 * 10) / 10;
  if (ms < 0 || ms > 500) throw new Error(`Messung nicht eindeutig (${String(ms).replace('.', ',')} ms). Bitte wiederholen, dabei leise sein.`);
  const p = inputPrefs();
  setInputPrefs({ measured: { ...(p.measured ?? {}), [inputInfo.label]: ms } });
  return { ms, quality: bestQ };
}

/** Kreuzkorrelation (normiert) über beide Kanäle; liefert die Position des Messsignals */
export function findProbe(channels: Float32Array[], probe: Float32Array): { idx: number; quality: number } {
  let best = -1, bestV = 0, energyP = 0;
  for (const v of probe) energyP += v * v;
  for (const ch of channels) {
    const N = ch.length - probe.length;
    // laufende Energie des Fensters
    let win = 0;
    for (let i = 0; i < probe.length && i < ch.length; i++) win += ch[i] * ch[i];
    for (let i = 0; i < N; i += 1) {
      let acc = 0;
      for (let j = 0; j < probe.length; j += 2) acc += ch[i + j] * probe[j];
      const v = (2 * acc) / Math.sqrt(energyP * Math.max(win, 1e-12));
      if (v > bestV) { bestV = v; best = i; }
      win += ch[i + probe.length] * ch[i + probe.length] - ch[i] * ch[i];
    }
  }
  return { idx: best, quality: Math.min(1, bestV) };
}

/** 24-Bit-WAV */
/** WAV (24 Bit) als Blob */
export function encodeWav(buf: AudioBuffer): Blob { return new Blob([wavBytes(buf)], { type: 'audio/wav' }); }
/** WAV (24 Bit) als Bytes */
export function wavBytes(buf: AudioBuffer): Uint8Array<ArrayBuffer> {
  const ch = buf.numberOfChannels, len = buf.length, bps = 3;
  const out = new DataView(new ArrayBuffer(44 + len * ch * bps));
  const w = (o: number, s: string) => [...s].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); out.setUint32(4, 36 + len * ch * bps, true); w(8, 'WAVE'); w(12, 'fmt ');
  out.setUint32(16, 16, true); out.setUint16(20, 1, true); out.setUint16(22, ch, true); out.setUint32(24, buf.sampleRate, true);
  out.setUint32(28, buf.sampleRate * ch * bps, true); out.setUint16(32, ch * bps, true); out.setUint16(34, 24, true); w(36, 'data'); out.setUint32(40, len * ch * bps, true);
  const data = Array.from({ length: ch }, (_, i) => buf.getChannelData(i));
  let o = 44;
  for (let i = 0; i < len; i++) for (let c = 0; c < ch; c++) {
    const v = Math.max(-1, Math.min(1, data[c][i])) * 0x7fffff | 0;
    out.setUint8(o++, v & 0xff); out.setUint8(o++, (v >> 8) & 0xff); out.setUint8(o++, (v >> 16) & 0xff);
  }
  return new Uint8Array(out.buffer);
}
